import * as React from "react";
import {
  Box,
  Button,
  Grid,
  useTheme,
  Hidden,
  Link as MUILink,
  Slider,
} from "@material-ui/core";
import Cropper from "react-easy-crop";
import {MUIDialog, Theme as theme, themeVariables} from "../..";
import { SvgIcon } from "../../../../../components";
import { CoverPreview } from "./CoverPreview";
import * as styles from "./styles.scss";
import {blobToFile, tl} from "../../../../../utils";

import getCroppedImg from "./cropimage";
import api from "../../../../../api";
import * as ReactDOM from "react-dom";
import {BrowserRouter} from "react-router-dom";
import {Provider} from "mobx-react";
import {ThemeProvider} from "@material-ui/core/styles";
import {MuiPickersUtilsProvider} from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import {Profile} from "../../../Profile";

interface IProps {
  className?:
    | "coursesGroup"
    | "siteBG"
    | "studentProfile"
    | "coursesTile"
    | "lessonCaption"
    | "lessonCaptionMobile"
    | "courseCaption"
    | "courseCaptionMobile"
    | "newsCover";
  onDelete?: (evt: any) => void;
  icon?: string;
  aspect?: number;
  cropSize?: { width: number; height: number };
  noResize?: boolean;
  changeHeight?: boolean;
  changeZoom?: boolean;
  hasPreview?: boolean;
  onChange: (evt: any) => void;
  cropDialogMaxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  noCropping?: boolean;
  courseId?: string | number;
  groupId?: string | number;
  fileId?: string | number;
  useSystem?: boolean;
  disabled?: boolean;
}

export const CoverUpload = ({
  className,
  onDelete,
  icon,
  aspect,
  cropSize,
  changeHeight,
  changeZoom,
  noResize,
  hasPreview,
  onChange,
  cropDialogMaxWidth,
  noCropping,
  courseId,
  fileId,
  groupId,
  useSystem,
  disabled,
}: IProps) => {
  const [crop, setCrop] = React.useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [rotation, setRotation] = React.useState<number>(0);
  const [zoom, setZoom] = React.useState<number>(1);
  const [cropSizeState, setCropSizeState] = React.useState<any>(cropSize);
  const [minZoom, setMinZoom] = React.useState<number>(1);
  const [mediadata, setMediadata] = React.useState<{
    width: number;
    height: number;
    naturalWidth: number;
    naturalHeight: number;
  }>();
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(null);
  const [aspectRatio, setAspectRatio] = React.useState<number>(aspect || 4 / 3);
  const [uploadedImageURL, setUploadedImageURL] = React.useState<string>("");
  const [uploadedImageType, setUploadedImageType] = React.useState<string>("");
  const [uploadedImageName, setUploadedImageName] = React.useState<string>("");

  const reloadState = () => {
    setMediadata(null);
    setAspectRatio(aspect || 4 / 3);
    setCroppedAreaPixels(null);
    setUploadedImageURL(null);
    setCropSizeState(cropSize);
    setZoom(1);
    setRotation(0);
  };

  const [inputId, setInputId] = React.useState<string>("coverUpload");
  const theme = useTheme();

  React.useEffect(() => {
    setInputId(`coverUpload-${Math.floor(Math.random() * 1e9)}`);
  }, []);

  React.useEffect(() => {
    if (noCropping && uploadedImageURL) {
      showCroppedImage();
      reloadState();
    }
  }, [uploadedImageURL]);

  const onCropComplete = React.useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onMediaLoaded = React.useCallback(({ ...data }) => {
    setMediadata(data);
    const minZoom = cropSize
      ? Math.max(cropSize.width / data.width, cropSize.height / data.height)
      : 1;
    setMinZoom(minZoom);
    setZoom(minZoom);
    if (noResize || !aspect) {
      setAspectRatio(data.width / data.height);
    }
  }, []);

  React.useEffect(() => {
    if (!mediadata) {
      return;
    }
    setAspectRatio(1 / aspectRatio);
    const rotatedSize =
      rotation % 180
        ? {
            width: mediadata.height,
            height: mediadata.width,
          }
        : {
            width: mediadata.width,
            height: mediadata.height,
          };
    const sizer = cropSize || rotatedSize;

    if (cropSize) {
      const minZoomUpdated = Math.max(
        sizer.width / rotatedSize.width,
        sizer.height / rotatedSize.height
      );
      const newMinZoom = rotation % 180 ? 1 / minZoomUpdated : minZoomUpdated;
      setMinZoom(minZoomUpdated);
      if (zoom < minZoomUpdated) {
        setZoom(minZoomUpdated);
      }
    } else {
      const minZoomUpdated = Math.max(
        sizer.width / mediadata.width,
        sizer.height / mediadata.height
      );
      const newMinZoom =
        rotation % 180 && mediadata.width > mediadata.height
          ? 1 / minZoomUpdated
          : 1;
      setMinZoom(newMinZoom);
      setZoom(newMinZoom);
    }
  }, [rotation, zoom]);

  const showCroppedImage = React.useCallback(async () => {
    try {
      const croppedImage:any = await getCroppedImg(
        uploadedImageURL,
        croppedAreaPixels,
        rotation,
        uploadedImageType
      );
      const imageFile = blobToFile(croppedImage,uploadedImageName);
      const file = await api.file.upload(imageFile, null, {
        course_id: courseId,
        group_id: groupId,
        file_id: fileId,
        useSystem: useSystem,
      });
      onChange(file.resultLink);
    } catch (e) {
      console.error(e);
    }
  }, [uploadedImageURL, croppedAreaPixels, rotation]);

  const controls =
    changeHeight || changeZoom ? (
      <Box
        mb={-2}
        mx={"auto"}
        pt={3}
        maxWidth={600}
        bgcolor={themeVariables.palette.common.white}
      >
        <Grid container alignItems="center" wrap="nowrap" spacing={1}>
          <Hidden xsDown>
            <Grid item>{tl("Изменить высоту:")}</Grid>
          </Hidden>
          <Grid item xs>
            <Box mt={1}>
              <Slider
                value={cropSizeState.height}
                min={10}
                max={cropSize.height}
                step={1}
                onChange={(e, cropHeight) =>
                  setCropSizeState({
                    width: cropSizeState.width,
                    height: cropHeight,
                  })
                }
              />
            </Box>
          </Grid>
          <Hidden xsDown>
            <Grid item>
              <Box pl={2.5}>{tl("Масштаб:")}</Box>
            </Grid>
          </Hidden>
          <Grid item>
            <MUILink
              onClick={() => {
                setZoom(zoom + 0.1);
              }}
            >
              <SvgIcon type="pdf-zoom-in" />
            </MUILink>
          </Grid>
          <Grid item>
            <MUILink
              onClick={() => {
                setZoom(zoom - 0.1);
              }}
            >
              <SvgIcon type="pdf-zoom-out" />
            </MUILink>
          </Grid>
        </Grid>
      </Box>
    ) : null;

  const coverBox = (
    <Box className={styles.wrap}>
      <input
        type="file"
        accept="image/*"
        className={styles.input}
        disabled={disabled}
        id={inputId}
        onChange={(evt: any) => {
          if (evt.target.files && evt.target.files.length > 0) {
            setUploadedImageType(evt.target.files[0].type);
            setUploadedImageName(evt.target.files[0].name);
            setUploadedImageURL(URL.createObjectURL(evt.target.files[0]));
          }
          evt.target.value = null;
          setZoom(1);
          setRotation(0);
          setCrop({ x: 0, y: 0 });
        }}
      />
      <Button
        component="label"
        htmlFor={inputId}
        variant="contained"
        className={`MuiButton-cover ${styles.button} ${styles[className]}`}
      >
        {icon ? (
          <Box
            bgcolor={theme.palette.background.paper}
            className={styles.cover}
          >
            <img src={icon} className={styles.image} />
            <Box bgcolor={theme.palette.grey.A700} className={styles.refresh}>
              <SvgIcon type="courses-renew" />
            </Box>
            {onDelete ? (
              <Box className={styles.deleteButton} onClick={onDelete}>
                <SvgIcon type="courses-delete white" />
              </Box>
            ) : null}
          </Box>
        ) : (
          <Box bgcolor={theme.palette.background.paper} className={styles.plus}>
            <SvgIcon type="fs-attach" />
          </Box>
        )}
      </Button>
      <MUIDialog
        maxWidth={cropDialogMaxWidth ? cropDialogMaxWidth : "md"}
        open={!noCropping && !!uploadedImageURL}
        onClose={() => reloadState()}
        actions={[
          [
            {
              label: tl("Отмена"),
              btnProps: {
                onClick: () => {
                  reloadState();
                },
              },
            },
          ],
          [
            {
              label: tl("Подтвердить"),
              btnProps: {
                color: "primary",
                onClick: () => {
                  showCroppedImage();
                  reloadState();
                },
              },
            },
          ],
        ]}
      >
        <Box my={-2} mx={-3} className={`${styles.crop} customWidth`}>
          <Cropper
            image={uploadedImageURL || ""}
            crop={crop}
            zoom={zoom}
            minZoom={minZoom}
            maxZoom={noResize ? minZoom : undefined}
            cropSize={cropSizeState}
            rotation={rotation}
            aspect={aspectRatio}
            onRotationChange={(evt: any, a: any) => {
              //   console.log(evt, a);
            }}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            onMediaLoaded={onMediaLoaded}
          />
          <Box pt={1} pr={1} className={styles.cropRotate}>
            <Grid container spacing={1}>
              <Grid item>
                <Button
                  variant="contained"
                  className="MuiButton-iconAction"
                  onClick={() => {
                    setRotation(rotation - 90);
                  }}
                >
                  <SvgIcon type="rotate-rcw" />
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  className="MuiButton-iconAction"
                  onClick={() => {
                    setRotation(rotation + 90);
                  }}
                >
                  <SvgIcon type="rotate-cw" />
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
        {controls}
      </MUIDialog>
    </Box>
  );

  return hasPreview ? (
    <Grid container spacing={1}>
      <Grid item>{coverBox}</Grid>
      <Grid item>
        <CoverPreview icon={icon} />
      </Grid>
    </Grid>
  ) : (
    coverBox
  );
};


(window as any).react_render_cover_upload = (nodeId: string, data: any) => {
  ReactDOM.render(
    <BrowserRouter>
        <ThemeProvider theme={theme}>
            <CoverUpload {...data} />
        </ThemeProvider>
    </BrowserRouter>,
    document.getElementById(nodeId)
  );
};

